import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, IconButton, Skeleton, Spinner, Tag, Typography } from '@volvo/vcdk-react';
import { useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import CampaignUnitsDetails from './_components/CampaignUnitsDetails';

import type { IAftermarketCampaign, IndicatorType } from '@/service/vpc/types/IAftermarketCampaign';
import type { IIndividualProduct } from '@/service/vpc/types/IIndividualProducts';
import type { IUnit } from '@/service/vpc/types/IUnit';
import type { ColumnDef } from '@tanstack/react-table';

import CampaignTypes from '@/components/CampaignTypes';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import FormatDateTime from '@/components/FormatDateTime';
import InformationWithCta from '@/components/InformationWithCta';
import ReleaseNotesModal from '@/components/modals/ReleaseNotesModal';
import SoftwareInstallationAvailableModal from '@/components/modals/SoftwareInstallationAvailableModal';
import Table from '@/components/Table';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useMediaQuery from '@/hooks/useMediaQuery';
import { getCombinedCampaignsQueryOptions, getIndividualProductsQueryOptions } from '@/service/vpc/queryOptions';
import { BLUE_DOT_STATUSES, PERFORMED_CAMPAIGN_STATUSES } from '@/service/vpc/types/IInstallableCampaign';
import { vpcService } from '@/service/vpc/vpcService';
import { chassis, common, indicator, rsdwl } from '@/utils';
import { dealerLocatorLink } from '@/utils/baseLinks';

const campaignsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).catch(1),
});

export const Route = createFileRoute('/_authenticated/_account/campaigns/')({
  component: CampaignsPage,
  errorComponent: ErrorBoundary,
  validateSearch: campaignsSearchSchema,
});

function CampaignsPage() {
  const { t } = useTranslation();
  const { mediaQueryNodes: mq } = useMediaQuery();

  const { fleetAccountUnits, isLoading, userHasPermission } = useFleetAccount();
  const hasSwDownload = userHasPermission('swdownload.read');
  const navigate = useNavigate({ from: '/campaigns/' });

  const endCustomerUnitIds = fleetAccountUnits.flatMap((unit) => String(unit.endCustomerUnitId)) || [];
  const serialNumbers = fleetAccountUnits.flatMap(chassis.getUnitSerialNumbers) || [];

  const { data: combinedCampaigns, isPending: isLoadingCampaigns } = useQuery(
    getCombinedCampaignsQueryOptions({ endCustomerUnitIds, serialNumbers })
  );

  const { data: productsData, isPending: isLoadingProducts } = useQuery(
    getIndividualProductsQueryOptions({ serialNumbers })
  );

  const { mutate: downloadCampaignsAsExcel, isPending } = useMutation({
    mutationFn: () => vpcService.Campaigns.downloadCampaignsAsExcel(),
    onSuccess: (data) => {
      const href = URL.createObjectURL(data);
      const today = new Date().toISOString().split('T')[0];
      const fileName = `${'accountName'}_campaigns_${today}.xlsx`;

      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', fileName);
      link.click();

      URL.revokeObjectURL(href);
    },
  });

  const activeCampaigns = combinedCampaigns?.filter(
    (_) => !PERFORMED_CAMPAIGN_STATUSES.has(_.data.concernedIndividualProductCampaignStatus)
  );

  // const performedCampaigns = combinedCampaigns?.filter((_) =>
  //   PERFORMED_CAMPAIGN_STATUSES.has(_.data.concernedIndividualProductCampaignStatus)
  // );

  const getIndividualProductBySerialNumber = useCallback(
    (serialNumber: string) => {
      return productsData?.find((product) => product.serialNumber === serialNumber);
    },
    [productsData]
  );

  const rowsActive = activeCampaigns?.map((c) => {
    const products = c.serials.map((serial) => getIndividualProductBySerialNumber(serial));
    const units = fleetAccountUnits.filter((u) =>
      chassis.getUnitSerialNumbers(u).includes(c.data.concernedIndividualProductSerialNumber)
    );
    return {
      data: c.data,
      units: units,
      products: products,
    };
  });

  // const rowsPerformed = performedCampaigns?.map((c) => {
  //   const products = c.serials.map((serial) => getIndividualProductBySerialNumber(serial));
  //   const units = fleetAccountUnits.filter((u) =>
  //     chassis.getUnitSerialNumbers(u).includes(c.data.concernedIndividualProductSerialNumber)
  //   );
  //   return {
  //     data: c.data,
  //     units: units,
  //     products: products,
  //   };
  // });

  // console.log(rowsPerformed);

  const columnsCampaigns = [
    {
      accessorKey: 'expand',
      id: 'expand',
      header: '',
      maxSize: 64,
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        order: 5,
      },
      cell: (info) => {
        return (
          <div className="">
            <IconButton
              icon={info.row.getIsExpanded() ? 'chevron-up' : 'chevron-down'}
              onClick={info.row.getToggleExpandedHandler()}
            />
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.data.aftermarketCampaignTitle,
      id: 'aftermarketCampaignTitle',
      header: t(($) => $.components.table.campaigns_table.campaign),
      sortingFn: 'alphanumeric',
      maxSize: 300,
      meta: {
        order: 0,
      },
      cell: (info) => {
        const row = info.row.original;
        const createdDate = row.data.createdDate;
        const validTo = row.data.validTo;
        return (
          <div className="flex items-center justify-between">
            <div>
              <Typography
                variant={mq({
                  default: 'heading4',
                  md: 'caption1',
                })}
                className="text-subtle whitespace-nowrap"
              >
                {info.getValue<string>()}
              </Typography>
              <div className="flex flex-row gap-2 sm:hidden">
                <FormatDateTime date={createdDate} type="short" variant="caption1" className="text-subtle" />
                -
                <FormatDateTime date={validTo} type="short" variant="caption1" className="text-subtle" />
              </div>
            </div>
            <ReleaseNotesModal
              campaignTitle={row.data.aftermarketCampaignTitle}
              campaignCode={row.data.installableCampaign?.campaignCode}
              campaignInformation={row.data.installableCampaign?.campaignInformation}
            />
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.data.aftermarketCampaignType,
      id: 'download',
      header: t(($) => $.components.table.campaigns_table.download),
      meta: {
        order: 4,
      },
      cell: (info) => {
        if (!hasSwDownload) return null;
        const campaign = info.row.original.data;
        const showWebInstallButton = rsdwl.showWebInstallButton(campaign);

        return showWebInstallButton ? (
          isMobile ? (
            <></>
          ) : (
            <SoftwareInstallationAvailableModal campaignTitle={campaign.aftermarketCampaignTitle} />
          )
        ) : null;
      },
    },
    {
      accessorFn: (row) => row.data.createdDate,
      id: 'createdDate',
      header: t(($) => $.components.table.campaigns_table.created_date),
      sortingFn: 'datetime',
      cell: (info) => {
        const createdDate = info.getValue<string>();
        return <FormatDateTime date={createdDate} type="short" variant="caption1" className="text-subtle" />;
      },
    },
    {
      accessorFn: (row) => row.data.validTo,
      id: 'validTo',
      header: t(($) => $.components.table.campaigns_table.valid_to),
      sortingFn: 'datetime',
      cell: (info) => {
        const validTo = info.getValue<string>();
        return <FormatDateTime date={validTo} type="short" variant="caption1" className="text-subtle" />;
      },
    },
    {
      accessorFn: (row) => row.data.concernedIndividualProductSerialNumber,
      id: 'product',
      header: t(($) => $.components.table.campaigns_table.product),
      sortingFn: 'alphanumeric',
      cell: (info) => {
        if (isLoadingProducts) {
          return <Skeleton variant="rounded-rectangle" height={18} width={100} />;
        }
        const serialNumber = info.getValue<string>();
        const product = getIndividualProductBySerialNumber(serialNumber);
        return (
          <Typography variant="caption1" className="text-subtle">
            {product?.productDesignation || ''}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.data.aftermarketCampaignType,
      id: 'aftermarketCampaignType',
      header: t(($) => $.components.table.campaigns_table.type),
      meta: {
        order: 3,
      },
      cell: (info) => {
        if (isLoadingCampaigns) {
          return <Skeleton variant="rounded-rectangle" height={18} width={150} />;
        }
        const campaignType = info.getValue<string>();
        const uniqueCampaigns = Array.from(new Set(campaignType.split('_'))) as IndicatorType[];
        const status = info.row.original.data.installableCampaign?.status;
        const visibleCampaigns = uniqueCampaigns.filter(
          (type) => type !== 'Installation' || (hasSwDownload && BLUE_DOT_STATUSES.has(status ?? ('' as never)))
        );
        return (
          <div className="flex gap-4">
            {visibleCampaigns.map((campaign) => (
              <Tag key={common.slugify(campaign)} variant={indicator.getSubtleVariant(campaign)}>
                {campaign}
              </Tag>
            ))}
          </div>
        );
      },
    },
  ] as ColumnDef<{
    data: IAftermarketCampaign;
    units: IUnit[];
    products: (IIndividualProduct | undefined)[];
  }>[];

  const mqSize = mq({
    default: 'mobile',
    md: 'desktop',
  });

  if (isLoading || isLoadingCampaigns || isLoadingProducts || !rowsActive) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <InformationWithCta
        storageKey="Notification_InformationWithCta"
        heading={t(($) => $.components.cta.notifications_cta.heading)}
        variant="info"
        closable={true}
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate({ to: '/settings' })}>
              {t(($) => $.components.cta.notifications_cta.notification_settings)}
            </Button>
          </>
        }
      >
        <Typography>{t(($) => $.components.cta.notifications_cta.message)}</Typography>
      </InformationWithCta>
      <Table
        key={`active-${mqSize}`}
        data={rowsActive || []}
        isLoading={isLoading || isLoadingCampaigns || isLoadingProducts}
        columns={columnsCampaigns}
        headerTitle={t(($) => $.pages.account.campaigns.active_table.title)}
        enableExpanding={true}
        expandingComponent={(row) => <CampaignUnitsDetails row={row} />}
        actions={
          <Button
            variant="secondary"
            iconEnd="download-cloud"
            loading={isPending}
            onClick={() => downloadCampaignsAsExcel()}
          >
            {t(($) => $.pages.account.campaigns.active_table.download_campaigns)}
          </Button>
        }
        showSearch={false}
        emptyState={{
          title: t(($) => $.pages.account.campaigns.active_table.empty_state.title),
        }}
      />
      {/* <Table
        key={`performed-${mqSize}`}
        data={rowsPerformed || []}
        isLoading={isLoading || isLoadingCampaigns || isLoadingProducts}
        columns={columnsCampaigns}
        headerTitle={t(($) => $.pages.account.campaigns.performed_table.title)}
        enableExpanding={true}
        expandingComponent={(row) => <CampaignUnitsDetails row={row} />}
        showSearch={false}
        emptyState={{
          title: t(($) => $.pages.account.campaigns.performed_table.empty_state.title),
        }}
      /> */}
      <CampaignTypes />
      <InformationWithCta
        storageKey="Campaigns_InformationWithCta"
        heading={t(($) => $.components.cta.action_cta.heading)}
        variant="info"
        closable={true}
        actions={
          <>
            <Button variant="secondary" href={dealerLocatorLink} target="_blank" rel="noopener noreferrer">
              {t(($) => $.components.cta.action_cta.find_dealer)}
            </Button>
            <Button variant="tertiary" onClick={() => navigate({ to: '/support' })}>
              {t(($) => $.components.cta.action_cta.learn_more)}
            </Button>
          </>
        }
      >
        <Typography>{t(($) => $.components.cta.action_cta.message)}</Typography>
      </InformationWithCta>
    </div>
  );
}
